import React, { useEffect, useMemo, useState } from 'react';
import CustomSelect from '../../components/CustomSelect';
import {
  dataSourceMapCn,
  dataSourceMapEn,
  dataSourceOptionsEn,
  dataSourceOptionsCn,
} from '../../constants/dataSource';
import {
  useGetCustomSelectButtonElement,
  useSetSelectDefaultValue,
} from '../../hooks/init';
import { SingleCommonType } from '../../types';
import { dataSourcePlaceholder } from '../../constants/placeholder';
import { commonBackInfo, highlight } from '../../utils';
import useLanguage from '@/pages/opinion/hooks/useLanguage';
import ChannelLabel, { ChannelIconType } from '../../../ChannelLabel';
import { OptionsType } from '../../utils/customSelectUtils';
import { getLocale } from 'umi';
import { FilterButtonPoint } from '@op/operationLog/pages/filter';

export default function DataSourceSelect({
  onChange,
  defaultValue,
  filterValue,
  defaultKey,
  lang,
  oriDefaultFilter,
  backOriDefaultValue,
}: SingleCommonType) {
  const locale = getLocale();

  const dataSourceMap = locale === 'en-US' ? dataSourceMapEn : dataSourceMapCn;
  const dataSourceOptions =
    locale === 'en-US' ? dataSourceOptionsEn : dataSourceOptionsCn;

  const [searchValue, setSearchValue] = useState('');

  const {
    selects: dataSourceSelects,
    setSelects: setDataSourceSelects,
    diffClassName,
  } = useSetSelectDefaultValue<string>({
    defaultValue,
    filterValue,
    oriDefaultFilter,
    backOriDefaultValue,
    defaultKey: 'data_source',
    options: dataSourceOptions.map((item) => item.value),
  });

  const { formatMessage } = useLanguage();

  const { customSelectRef, getButtonElement } =
    useGetCustomSelectButtonElement();

  useEffect(() => {
    const [text, isAll] = getButtonElement();
    onChange?.(
      'data_source',
      commonBackInfo(
        { data_source: dataSourceSelects },
        { [defaultKey]: text },
        { isAll, needChangeEntity: true },
      ),
    );
  }, [dataSourceSelects, formatMessage]);

  const dataSourceValue = useMemo(() => {
    return dataSourceSelects.map(String);
  }, [dataSourceSelects]);

  const options: OptionsType = useMemo(() => {
    return dataSourceOptions.filter(({ text }) =>
      text.toLowerCase().includes(searchValue.toLowerCase()),
    );
  }, [searchValue, locale]);

  return (
    <div className={`data-source-select-wrapper ${diffClassName}`}>
      <CustomSelect
        allLabel={formatMessage({ id: 'Data Source' })}
        allShowModal="text"
        searchInfo={[searchValue, setSearchValue]}
        ref={customSelectRef}
        pointKey={FilterButtonPoint.dataSource}
        placeholderText={dataSourcePlaceholder[lang]}
        onClear={() => setDataSourceSelects([])}
        buttonItemRender={(item: ChannelIconType) => {
          return (
            <div className="dbo-select-channel-item">
              <ChannelLabel
                className="dbo-select-channel-icon"
                channel_name={item.toLowerCase() as ChannelIconType}
                showText={false}
              ></ChannelLabel>
              <span>{highlight(dataSourceMap[item], searchValue)}</span>
            </div>
          );
        }}
        optionItemRender={(_: any, item: ChannelIconType) => {
          return (
            <div className="dbo-select-channel-item">
              <ChannelLabel
                className="dbo-select-channel-icon"
                channel_name={item.toLowerCase() as ChannelIconType}
                showText={false}
              ></ChannelLabel>
              <span>{highlight(dataSourceMap[item], searchValue)}</span>
            </div>
          );
        }}
        selectOptionsInfo={[dataSourceValue, setDataSourceSelects]}
        options={options}
      ></CustomSelect>
    </div>
  );
}
